import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import { useCookies } from "react-cookie";
import { parseISO, addMinutes, getUnixTime } from "date-fns";
import { Box, Alert, Typography, Button, Dialog } from "@mui/material";
import { formatInTimeZone } from "date-fns-tz";

import { includeSlotsWith } from "utils/services/available-checker";

import {
  ServiceType,
  ServiceInfoType,
  ServiceCommon,
  AvailableSlotsNew as Slot,
  BookingContextType,
  stillAvailable,
  parkSlot,
  Available,
} from "utils/booking";

import Slots from "./slots";

// context
import { bookFreeConsult, useBookingContext } from "hooks/useBooking";
import { getTrackers } from "utils/utmParser";
import RepickDialog from "components/RepickDialog";
import { LoadingButton } from "@mui/lab";
// ======================================================

const shuffle = (array: Available[]) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};
const removeDuplicates = (slots: Available[]) => {
  const uniqueIds = new Set();

  const unique = shuffle(slots).filter((element: Available) => {
    const isDuplicate = uniqueIds.has(element.time);

    uniqueIds.add(element.time);

    if (!isDuplicate) {
      return true;
    }

    return false;
  });

  return unique.sort(
    (a, b) => parseISO(a.orgTime).getTime() - parseISO(b.orgTime).getTime(),
  );
};

type TAvailableSlots = {
  morning: Available[];
  afternoon: Available[];
  evening: Available[];
};

const AvailableSlots = ({
  slots,
  isEmpty,
  isLoading,
  dpc,
  timeZone,
  info,
  freeConsult,
  reschedule,
  payToResched,
  repick,
  preloaded,
  serviceId,
  selectedDay,
  isITH = false,
}: {
  slots: Slot[];
  isEmpty: boolean;
  isLoading: boolean;
  dpc: ServiceType;
  info: ServiceInfoType;
  freeConsult: ServiceCommon;
  timeZone: string;
  reschedule?: boolean;
  payToResched?: boolean;
  serviceId?: string;
  repick?: boolean;
  preloaded?: boolean;
  selectedDay: string;
  isITH?: boolean;
}) => {
  const { push, query } = useRouter();
  const cookies = useCookies();
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [available, setAvailable] = useState<TAvailableSlots>({
    morning: [],
    afternoon: [],
    evening: [],
  });

  const [pickAgain, setRepick] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [slot, setSlot] = useState<Available>();
  const context = useBookingContext() as BookingContextType;

  useEffect(() => {
    let rawData: Available[] = [];
    if (slots && slots?.length > 0) {
      slots
        .filter((x) => x.staffId !== "" && x.date === selectedDay)
        .forEach((item) => {
          const items = item.available?.map((time) => {
            return {
              timeZone:
                timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
              date: item!.date,
              id: item!.id,
              orgTime: time,
              time: formatInTimeZone(
                time,
                timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
                "p",
              ),
              staffId: item.staffId,
              staffEmail: item.staffEmail,
              hour: Number(
                formatInTimeZone(
                  time,
                  timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
                  "HH",
                ),
              ),
            };
          });
          rawData.push(...items);
        });
    }

    const minimum =
      dpc?.Minimum_Booking_Notice_Min ?? info?.Minimum_Booking_Notice_Min ?? 0;

    rawData = rawData
      // .sort((a, b) => a.hour - b.hour)
      .filter(
        (t) =>
          getUnixTime(parseISO(t.orgTime)) >
          getUnixTime(
            addMinutes(
              parseISO(
                formatInTimeZone(new Date(), "UTC", "yyyy-MM-dd HH:mm:ssXXX"),
              ),
              minimum,
            ),
          ),
      );

    const morning = includeSlotsWith(
      removeDuplicates(rawData?.filter((t) => t.hour < 12)) || [],
      info,
    );

    const afternoon = includeSlotsWith(
      removeDuplicates(rawData?.filter((t) => t.hour >= 12 && t.hour < 18)) ||
        [],
      info,
    );

    const evening = includeSlotsWith(
      removeDuplicates(rawData?.filter((t) => t.hour >= 18)) || [],
      info,
    );

    setAvailable({ morning, afternoon, evening });
  }, [
    context.data.services,
    info,
    selectedDay,
    slots,
    timeZone,
    dpc?.Minimum_Booking_Notice_Min,
    query.service,
  ]);

  const bookAppointment = async (payload: ServiceCommon) => {
    setSaving(true);
    const { isError, data } = await bookFreeConsult(payload);
    if (!isError) {
      if (
        info.required_documents &&
        info.required_documents[0].length >= 1 &&
        freeConsult.cost > 0
      ) {
        push("/services/required-docs");
      } else push("/services/success");
    } else {
      // console.log({ isError });
    }
    setSaving(false);
    return data?.zoho ?? "";
  };

  const onSelectSlot = (slot: Available) => {
    if (isITH) {
      setSlot(slot);
      const now = new Date();

      const originalDate = new Date(slot.orgTime);

      const timeDiff =
        (originalDate.getTime() - now.getTime()) / (60 * 60 * 1000);

      const isNotValid = timeDiff > 0 && timeDiff < 24;

      if (!isNotValid) handleSelectSlot(slot);

      setShowDialog(isNotValid);
    } else {
      setSlot(slot);
      handleSelectSlot(slot);
    }
  };

  const handleSelectSlot = async (slot?: Available) => {
    if (!slot) return;
    setShowDialog(false);
    setVerifying(true);

    const parked = await parkSlot(
      slot.staffEmail,
      slot.staffId,
      slot.orgTime,
      "",
      info.Duration_Min,
      slot.timeZone,
    );

    const canBook = await stillAvailable({
      staffId: slot.staffId,
      email: slot.staffEmail,
      duration: info.Duration_Min,
      buffer: info.Buffer_Time_Min,
      start: slot.orgTime,
      timeZone,
      selectedSlot: slot.orgTime
        ? {
            time: slot.orgTime,
            minBooking: info?.Minimum_Booking_Notice_Min ?? 0,
          }
        : undefined,
    });
    if (!canBook || !parked?.details?.available) {
      setRepick(formatInTimeZone(slot.orgTime, timeZone, "yyyy-MM-dd p"));
      setVerifying(false);
      return;
    }
    setVerifying(false);
    const trackers = getTrackers(cookies, query);
    const startTime = formatInTimeZone(
      slot.orgTime,
      timeZone,
      "yyyy-MM-dd HH:mm:ssXXX",
    );

    const endTime = addMinutes(
      parseISO(startTime as string),
      info.Duration_Min + info?.Buffer_Time_Min,
    );

    const cost = Number(info?.Cost) ?? 0;

    const param: ServiceCommon = {
      ...freeConsult,
      // CRM_Meeting_ID:
      Start_time: parseISO(startTime),
      patient_time: parseISO(startTime),
      End_time: endTime,
      channel: info.Channel,
      service_type: info.service_name,
      duration: info.Duration_Min,
      buffer: info.Buffer_Time_Min,
      cost: info.Cost,
      Bookings_Staff_ID: dpc ? dpc.staff_id : slot.staffId,
      googleCalendarId: dpc ? dpc.staff_email : slot.staffEmail,
      staffId: dpc ? dpc.staff_id : slot.staffId,
      serviceId: dpc ? dpc.service_id : serviceId ?? "",
      patient_timezone:
        timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      meeting_status: !reschedule ? "upcoming" : "Reschedule",
    };
    if (reschedule && payToResched) {
      context.setFreeConsult({ ...trackers, ...param });
      push("/services/reschedule-payment");
    } else if (!reschedule && !repick && !freeConsult?.preloaded) {
      context.setFreeConsult({ ...trackers, ...param });
      push("/services/contact-info");
    } else if (
      !reschedule &&
      !repick &&
      (preloaded || freeConsult?.preloaded) &&
      cost === 0 &&
      info?.Additional_Questions &&
      info?.Additional_Questions?.length > 0
    ) {
      context.setFreeConsult({ ...trackers, ...param });
      push("/services/additional-questions");
    } else if (
      !reschedule &&
      !repick &&
      (preloaded || freeConsult?.preloaded) &&
      cost === 0
    ) {
      const meetingId = await bookAppointment(param);
      context.setFreeConsult({
        ...trackers,
        ...param,
        CRM_Meeting_ID: meetingId,
      });
    } else if (
      (!reschedule && !repick && (preloaded || freeConsult?.preloaded)) ||
      info?.capture_card === true
    ) {
      context.setFreeConsult({ ...trackers, ...param });
      push("/services/payment");
    } else if (reschedule || repick) {
      context.setFreeConsult({ ...trackers, ...param });
      push("/services/reschedule");
    }
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <RepickDialog
          showRepick={pickAgain !== null && pickAgain !== ""}
          slot={pickAgain ? pickAgain : ""}
          title="Slot no longer Available"
          onPick={() => setRepick(null)}
        />
        {saving && (
          <Box sx={{ p: 1, my: 2 }}>
            <Typography align="center" fontFamily="ABCDiaTypeRegular">
              Please wait...
            </Typography>
          </Box>
        )}
        {!saving && (
          <>
            <Slots
              submitting={false}
              verifying={verifying}
              slots={available.morning}
              caption="Morning"
              handleSelectSlot={onSelectSlot}
            />
            <Slots
              submitting={false}
              verifying={verifying}
              slots={available.afternoon}
              caption="Afternoon"
              handleSelectSlot={onSelectSlot}
            />
            <Slots
              submitting={false}
              verifying={verifying}
              slots={available.evening}
              caption="Evening"
              handleSelectSlot={onSelectSlot}
            />
          </>
        )}
        {isEmpty && !isLoading && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {
              "Unfortunately there are no slots available for this period. Please click > to see next week’s availability. "
            }
          </Alert>
        )}
      </Box>

      <Dialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            padding: "16px",
            width: "377px",
            alignItems: "center",
            gap: "24px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
              marginTop: "24px",
            }}
          >
            <Typography
              variant="h3"
              fontWeight={"bold"}
              fontSize={"16px"}
              color={"#EB7141"}
            >
              Warning
            </Typography>

            <Typography
              variant="h3"
              fontWeight={"bold"}
              fontSize={"24px"}
              textAlign={"center"}
              lineHeight={"30.02px"}
            >
              The selected appointment time occurs in the next 24 hours
            </Typography>

            <Typography
              variant="h3"
              fontWeight={"normal"}
              fontSize={"16px"}
              textAlign={"center"}
              lineHeight={"22.4px"}
            >
              {`Please acknowledge that it is required you provide your driver’s license and insurance details prior to your appointment time or your appointment will be canceled.`}
            </Typography>
          </Box>

          <Button
            onClick={() => setShowDialog(false)}
            variant="contained"
            sx={{
              backgroundColor: "#3D4732",
              borderRadius: "8px",
              padding: "10px",
              width: "100%",
              textTransform: "none",
            }}
          >
            <Typography
              variant="h3"
              fontSize={"18px"}
              textAlign={"center"}
              color={"#FCFCFA"}
            >
              {`Choose Different Time`}
            </Typography>
          </Button>

          <LoadingButton
            loading={verifying || false}
            onClick={() => handleSelectSlot(slot)}
            variant="text"
            sx={{
              textTransform: "none",
            }}
          >
            {!verifying && (
              <Typography
                variant="h3"
                fontWeight={"normal"}
                fontSize={"16px"}
                textAlign={"center"}
                color={"#768464"}
                sx={{
                  textDecoration: "underline",
                }}
              >
                I acknowledge. Continue to Booking.
              </Typography>
            )}
          </LoadingButton>
        </Box>
      </Dialog>
    </>
  );
};

export default AvailableSlots;
